import { createRef, Component, Fragment } from "react";
import {
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from "prop-types";

import { URL_API_WATCH } from "../../constants";
import {
  api,
  getApiErrorMessage,
  scrollIfNeeded,
  validateInput,
} from "../../helpers";

import Alert from "../../styles/components/Alert";
import Button from "../../styles/components/Button";
import EmailOptIn from "../email-opt-in";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal from "../modal";
import Recaptcha from "../recaptcha";
import { RecaptchaDisclaimer } from "../recaptcha/components";
import Text from "../form/components/Text";

class ModalWatch extends Component {
  constructor(props) {
    super(props);

    this.getRecaptchaToken = this.getRecaptchaToken.bind(this);
    this.handleApiError = this.handleApiError.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.postData = this.postData.bind(this);

    this.form = createRef();
    this.header = createRef();

    this.state = {
      email: "",
      emailError: null,
      inProgress: false,
      serverError: null,
      success: false,
    };
  }

  /**
   * Get reCaptcha token
   * @public
   */
  getRecaptchaToken() {
    const { handleApiError } = this;
    const { recaptchaLoaded, requestRecaptchaToken } = this.props;

    if (!recaptchaLoaded || !requestRecaptchaToken) {
      this.handleApiError("reCAPTCHA did not load");
      return false;
    }

    requestRecaptchaToken("watch")
      .then(token => {
        if (!token) {
          handleApiError("reCAPTCHA failed to execute");
          return false;
        }

        this.postData(token);
      })
      .catch(error => handleApiError(error));
  }

  /**
   * Handle API error
   * @param {*}
   * @public
   */
  handleApiError(error) {
    const message = getApiErrorMessage(error);

    this.setState({ inProgress: false, serverError: message }, () =>
      scrollIfNeeded(this.header.current)
    );
  }

  /**
   * Handle input change
   * @param {event|object} e
   * @public
   */
  handleInput(e) {
    const { target: input } = e;
    const field = input.id;
    const value = !!input ? input.value : null;
    let error = validateInput(input);

    if (!!field) {
      this.setState({ [field]: value, [`${field}Error`]: error });
    }
  }

  /**
   * Handle form submission
   * @param {event} e
   * @public
   */
  handleSubmit(e) {
    e.preventDefault();

    const { current: form } = this.form;

    if (!form) {
      return false;
    }

    if (!form.checkValidity()) {
      [...form.elements].forEach(input => {
        this.handleInput({ target: input });
      });
    } else {
      this.getRecaptchaToken();
    }
  }

  /**
   * Post data to API
   * @public
   */
  postData(recaptchaToken) {
    this.setState({ inProgress: true });

    const { handleApiError } = this;
    const { emailOptIn, listingId, trackEvent } = this.props;
    const { email } = this.state;

    if (!listingId && parseInt(listingId, 10) !== 0) {
      handleApiError("No listing specified");
      return false;
    }

    const data = {
      id: 0,
      listingId: listingId,
      email: email,
      emailOptIn: emailOptIn,
      recaptchaToken: recaptchaToken,
      userId: 0,
    };

    api
      .post(URL_API_WATCH, data)
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (!!trackEvent) {
            trackEvent({
              category: "Form",
              action: "Submit",
              label: `${this.props.analyticsLabel} Watch Listing Modal`,
            });
          }

          this.setState(
            {
              inProgress: false,
              serverError: null,
              success: true,
            },
            () => {
              scrollIfNeeded(this.header.current);
            }
          );
        } else {
          handleApiError(response);
        }
      })
      .catch(handleApiError);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.open && prevProps.open) {
      this.setState({
        serverError: null,
        success: false,
      });
      if (this.props.analyticsLabel !== "detail") {
        this.props.clearDataLayerListingParameters();
      }
    }

    if (!prevProps.open && this.props.open && !!this.props.trackModalView) {
      this.props
        .addDataLayerListingParameters([this.props.listing])
        .then(() => {
          this.props.trackModalView("watch", {
            category: "click",
            action: "Watch Listing",
            label: this.props.analyticsLabel,
          });
        });
    }
  }

  render() {
    const { form, handleInput, handleSubmit, header } = this;
    const { close, open, targetDevice } = this.props;
    const { email, emailError, inProgress, serverError, success } = this.state;

    return (
      <Modal aria-label="Watch" close={close} open={open} size="small">
        <Recaptcha />
        <header ref={header}>
          {!success ? (
            <Fragment>
              <h1 className={!!serverError ? "" : "mb-0"}>
                <strong>Watch Listing</strong>
              </h1>
            </Fragment>
          ) : (
            <Fragment>
              <h1>
                <strong>You Are Now Watching This Listing</strong>
              </h1>
              <p className="mb-0">You are now watching this listing.</p>
            </Fragment>
          )}
          {serverError && <Alert type="error">{serverError}</Alert>}
        </header>
        {success && (
          <Button
            block={targetDevice === "mobile"}
            color="blue500"
            onClick={close}
            size="small"
            type="button"
          >
            Return to Listings
          </Button>
        )}
        {!success && (
          <Form onSubmit={handleSubmit} formRef={form}>
            <div className="form-fields">
              <Group error={emailError}>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <Text
                  id="email"
                  maxLength="200"
                  onChange={handleInput}
                  required
                  type="email"
                  value={email}
                />
              </Group>
              <EmailOptIn labelOffset="small" />
            </div>
            <RecaptchaDisclaimer className="form-disclaimer" />
            <div className="form-actions">
              <Button
                block={targetDevice === "mobile"}
                color="green400"
                disabled={inProgress}
                size="small"
                square={targetDevice === "mobile"}
                type="submit"
              >
                {inProgress ? "Watching…" : "Watch"}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    );
  }
}

ModalWatch.propTypes = {
  addDataLayerListingParameters: func.isRequired,
  addWatchedListing: func.isRequired,
  clearDataLayerListingParameters: func.isRequired,
  analyticsLabel: string,
  close: func.isRequired,
  emailOptIn: bool,
  history: object.isRequired,
  listing: object,
  listingsId: oneOfType([number, string]),
  location: object.isRequired,
  open: bool,
  recaptchaLoaded: bool,
  requestRecaptchaToken: func.isRequired,
  targetDevice: string,
  user: shape({
    email: string,
  }),
};

ModalWatch.defaultProps = {
  analyticsLabel: "modal",
  open: false,
};

export default ModalWatch;
